import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext();

// Build a deterministic cartKey from id + selected_options so the same item
// with different option combos appears as separate cart lines.
const makeCartKey = (menuItem) => {
  if (menuItem.selectedOptions && menuItem.selectedOptions.length > 0) {
    const sig = menuItem.selectedOptions
      .map(o => `${o.group_id || ''}:${o.label_es || ''}`)
      .join('|');
    return `${menuItem.id}_${sig}`;
  }
  return `${menuItem.id}_${menuItem.selectedOption || 'default'}`;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  const addItem = useCallback((menuItem) => {
    setItems(prev => {
      const cartKey = makeCartKey(menuItem);
      const existing = prev.find(item => item.cartKey === cartKey);
      if (existing) {
        return prev.map(item =>
          item.cartKey === cartKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...menuItem, cartKey, quantity: 1 }];
    });
  }, []);

  const removeItem = useCallback((cartKey) => {
    setItems(prev => prev.filter(item => item.cartKey !== cartKey));
  }, []);

  const updateQuantity = useCallback((cartKey, quantity) => {
    if (quantity <= 0) {
      removeItem(cartKey);
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.cartKey === cartKey ? { ...item, quantity } : item
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  // Sum extras either from selectedOptions[] (new) or legacy extraPrice
  const getExtra = (item) => {
    if (item.selectedOptions && item.selectedOptions.length > 0) {
      return item.selectedOptions.reduce((s, o) => s + (Number(o.extra_price) || 0), 0);
    }
    return item.extraPrice || 0;
  };

  const total = items.reduce((sum, item) => sum + ((item.price + getExtra(item)) * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      total,
      itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
